import { ElementType, useRef } from 'react';
import Tippy from '@tippyjs/react';

import { Ripple } from '..';
import { renderComponent, classes, splitValueUnit } from 'utils';
import { call } from 'utils';

import 'tippy.js/dist/tippy.css';
import 'tippy.js/dist/svg-arrow.css';
import 'tippy.js/animations/perspective.css';

import styles from './IconButton.module.scss';

type Props = {
  icon: ElementType;
  iconProps?: object;
  onClick?: () => void;
  size?: 'string' | number;
  rate?: number;
  shape?: 'square' | 'circle';
  type?: 'regular' | 'disabled';
  overflow?: boolean;
  hint?: string;
};

const IconButton = (props: Props) => {
  const { size = '1.5rem', rate = 1, shape = 'circle' } = props;
  const { overflow = true, hint, type = 'regular' } = props;

  const ref = useRef<HTMLDivElement>(null);
  const { value, unit } = splitValueUnit(size);

  const style = {
    width: size,
    height: size,
    fontSize: `${value * rate}${unit}`,
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && ref.current) {
      ref.current.click();
    }
  };

  const content = (
    <div
      ref={ref}
      className={classes(
        styles.iconButton,
        styles[shape],
        styles[type],
        overflow ? styles.overflow : null
      )}
      onClick={e => {
        e.stopPropagation();
        call(props.onClick);
      }}
      tabIndex={type !== 'disabled' ? 0 : -1}
      onKeyDown={onKeyDown}
    >
      <div style={style} tabIndex={-1}>
        {renderComponent(props.icon, props.iconProps)}
        <Ripple parentRef={ref} shape={shape} size={overflow ? '1.5w' : '1w'} />
      </div>
    </div>
  );

  return hint ? (
    <Tippy
      className={classes(styles.popup, styles[type])}
      content={hint}
      animation="perspective"
      touch={false}
    >
      {content}
    </Tippy>
  ) : (
    content
  );
};

export default IconButton;
